export const INDICATOR_TEMPLATES_STORAGE_KEY = 'erevis.chart.indicatorTemplates.v1'
export const INDICATOR_TEMPLATES_EVENT = 'erevis:indicator-templates-changed'

function clonePlain(value) {
  if (value == null || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map(clonePlain)
  const out = {}
  for (const key of Object.keys(value)) out[key] = clonePlain(value[key])
  return out
}

function storage() {
  return typeof localStorage === 'undefined' ? null : localStorage
}

function nowIso() {
  return new Date().toISOString()
}

function makeId(kind) {
  return `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function normalizeIndicatorTemplate(entry) {
  if (!entry || typeof entry !== 'object') return null
  if (typeof entry.id !== 'string' || entry.id.length === 0) return null
  if (typeof entry.name !== 'string' || entry.name.trim().length === 0) return null
  if (typeof entry.kind !== 'string' || entry.kind.length === 0) return null
  if (!entry.params || typeof entry.params !== 'object' || Array.isArray(entry.params)) return null
  if (!entry.style || typeof entry.style !== 'object' || Array.isArray(entry.style)) return null

  return {
    id: entry.id,
    name: entry.name.trim(),
    kind: entry.kind,
    params: clonePlain(entry.params),
    style: clonePlain(entry.style),
    createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : null,
    updatedAt: typeof entry.updatedAt === 'string' ? entry.updatedAt : null,
    source: entry.source && typeof entry.source === 'object' && !Array.isArray(entry.source)
      ? clonePlain(entry.source)
      : null,
  }
}

export function listIndicatorTemplates({ kind } = {}) {
  const store = storage()
  if (!store) return []
  const raw = store.getItem(INDICATOR_TEMPLATES_STORAGE_KEY)
  if (raw == null) return []
  try {
    const parsed = JSON.parse(raw)
    const arr = Array.isArray(parsed?.templates) ? parsed.templates : Array.isArray(parsed) ? parsed : []
    return arr
      .map(normalizeIndicatorTemplate)
      .filter(Boolean)
      .filter((template) => (kind ? template.kind === kind : true))
      .sort((a, b) => a.name.localeCompare(b.name))
  } catch {
    return []
  }
}

function writeTemplates(templates) {
  const store = storage()
  if (!store) return false
  try {
    store.setItem(INDICATOR_TEMPLATES_STORAGE_KEY, JSON.stringify({
      version: 1,
      templates,
    }))
    return true
  } catch {
    return false
  }
}

export function notifyIndicatorTemplatesChanged() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(INDICATOR_TEMPLATES_EVENT))
}

export function subscribeIndicatorTemplates(listener) {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(INDICATOR_TEMPLATES_EVENT, listener)
  window.addEventListener('storage', listener)
  return () => {
    window.removeEventListener(INDICATOR_TEMPLATES_EVENT, listener)
    window.removeEventListener('storage', listener)
  }
}

export function saveIndicatorTemplateFromIndicator(indicator, name, source = {}) {
  if (!indicator || typeof indicator !== 'object') return null
  const cleanName = String(name || '').trim()
  if (!cleanName) return null
  const kind = indicator.kind
  if (typeof kind !== 'string' || !kind) return null
  if (!indicator.params || !indicator.style) return null

  const templates = listIndicatorTemplates()
  const existingIndex = templates.findIndex((template) => (
    template.kind === kind && template.name.toLowerCase() === cleanName.toLowerCase()
  ))
  const existing = existingIndex >= 0 ? templates[existingIndex] : null
  const timestamp = nowIso()
  const next = {
    id: existing?.id || makeId(kind),
    name: cleanName,
    kind,
    params: clonePlain(indicator.params),
    style: clonePlain(indicator.style),
    createdAt: existing?.createdAt || timestamp,
    updatedAt: timestamp,
    source: source && typeof source === 'object' ? clonePlain(source) : null,
  }

  if (existingIndex >= 0) templates[existingIndex] = next
  else templates.push(next)
  if (!writeTemplates(templates)) return null
  notifyIndicatorTemplatesChanged()
  return next
}

export function createIndicatorFromTemplate(template, presetId) {
  const normalized = normalizeIndicatorTemplate(template)
  if (!normalized || typeof presetId !== 'string' || presetId.length === 0) return null
  return {
    presetId,
    kind: normalized.kind,
    params: clonePlain(normalized.params),
    style: clonePlain(normalized.style),
  }
}
